import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, AUTH_BG_URL } from "@/lib/assets";
import { toast } from "sonner";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/member";

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      nav(u.role === "admin" || u.role === "content_manager" ? "/admin" : next);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  return <AuthShell>
    <form onSubmit={submit} className="max-w-md w-full">
      <Link to="/"><img src={LOGO_URL} alt="Judge Auntie" className="h-16 mb-8" /></Link>
      <h1 className="brand-h2">Welcome back.</h1>
      <p className="text-charcoal/60 mt-2">Sign in to your seat at Auntie's table.</p>
      <div className="mt-8 space-y-4">
        <label className="block">
          <span className="text-sm">Email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </label>
        <label className="block">
          <span className="text-sm">Password</span>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </label>
        {err && <div className="text-burgundy text-sm" data-testid="login-error">{err}</div>}
        <button disabled={busy} className="btn-primary w-full" data-testid="login-submit">{busy ? "…" : "Sign in"}</button>
      </div>
      <div className="mt-6 text-sm text-charcoal/60">
        New here? <Link to="/register" className="text-plum underline" data-testid="login-register-link">Create your account</Link>
      </div>
    </form>
  </AuthShell>;
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await register(form.email, form.password, form.name);
      toast.success("Come sit with Judge Auntie™.");
      nav("/member");
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not create account");
    } finally { setBusy(false); }
  };

  return <AuthShell>
    <form onSubmit={submit} className="max-w-md w-full">
      <Link to="/"><img src={LOGO_URL} alt="Judge Auntie" className="h-16 mb-8" /></Link>
      <h1 className="brand-h2">Create your account.</h1>
      <p className="text-charcoal/60 mt-2">Free forever — upgrade any time.</p>
      <div className="mt-8 space-y-4">
        <label className="block">
          <span className="text-sm">Full name</span>
          <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="register-name"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </label>
        <label className="block">
          <span className="text-sm">Email</span>
          <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="register-email"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </label>
        <label className="block">
          <span className="text-sm">Password (min 8 chars)</span>
          <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="register-password"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </label>
        {err && <div className="text-burgundy text-sm" data-testid="register-error">{err}</div>}
        <button disabled={busy} className="btn-primary w-full" data-testid="register-submit">{busy ? "…" : "Create my account"}</button>
      </div>
      <div className="mt-6 text-sm text-charcoal/60">
        Have an account? <Link to="/login" className="text-plum underline" data-testid="register-login-link">Sign in</Link>
      </div>
    </form>
  </AuthShell>;
}

function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <div className="flex items-center justify-center p-8">{children}</div>
      <div className="relative hidden lg:block">
        <img src={AUTH_BG_URL} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-plum/60" />
        <div className="absolute inset-0 p-14 flex flex-col justify-end text-cream">
          <div className="script-auntie text-6xl text-gold">Auntie</div>
          <blockquote className="mt-4 font-serif text-3xl leading-tight max-w-md">
            "The court may be adjourned — but Judge Auntie™ still has the mic."
          </blockquote>
        </div>
      </div>
    </div>
  );
}
